import { Component, effect, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ButtonComponent } from 'src/app/components/shared/button';
import { environment } from 'src/environments/environment';
import { PaymentSection } from './section';
import { CommonModule } from '@angular/common';
import { fetchAuthSession, fetchUserAttributes } from '@aws-amplify/auth';
import { Router } from '@angular/router';
import { SharedStateService } from 'src/app/services/category.service';
import { LocaleService } from 'src/app/services/locale';
import { APIService } from 'src/app/services/api';
import { PaymentService } from 'src/app/services/payment';
import { UserService } from 'src/app/services/user';
const PREVIEW_MODE_KEY = 'isPreviewMode';

@Component({
  selector: 'report-payment',
  imports: [ButtonComponent, PaymentSection, CommonModule],
  template: `
    <div
      (click)="$event.stopPropagation()"
      class="m-auto w-[700px] mt-30 bg-white rounded-lg px-12 py-6 flex flex-col gap-3 z-50"
    >
      <div class="flex flex-col gap-3">
        <div
          *ngIf="!paidForLogo"
          class="text-lg text-center w-full futura"
        >
          {{ locale.report.customised_report }}
        </div>
        <div class="grid grid-cols-2 gap-3">
          <report-payment-section
            [text]="locale.report.company_logo"
            [logo]="companyLogo"
            [selectLogo]="selectLogo"
            [logoSelected]="logoSelected"
            [resetLogo]="resetLogo"
          ></report-payment-section>
          <report-payment-section
            [text]="locale.report.profile_logo"
            [logo]="profilePhoto"
            [selectLogo]="selectLogo"
            [logoSelected]="logoSelected"
            [resetLogo]="resetLogo"
          ></report-payment-section>
        </div>
        <div class="px-3 bg-[#6a7282]/40 my-2 h-[1px] w-full"></div>
        <div>
          <div
            *ngIf="!paidForReport"
            class="text-lg text-center w-full futura mb-4"
          >
            {{ locale.report.download_report }}
          </div>
          <div class="w-fit mx-auto">
            <app-button (click)="previewReport()" [text]="locale.report.generate_report"></app-button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class Payment implements OnInit {
  @Input() report: any;
  @Output() close = new EventEmitter<void>();
  @Output() preview = new EventEmitter<boolean>();
  logoSelected = false;

  companyLogo = localStorage.getItem('companyLogo');
  profilePhoto: any = localStorage.getItem('profilePhoto');

  reportPrice = environment.stripePrices.report;

  paidForLogo = false;
  paidForReport = false;

  private localeService = inject(LocaleService);
  locale = this.localeService.locale();

  private api = inject(APIService);
  private paymentService = inject(PaymentService);
  private userService = inject(UserService);

  constructor(private spinner: NgxSpinnerService, private router: Router, public currentcat: SharedStateService) {
    effect(() => {
      this.locale = this.localeService.locale();
    });
  }

  private planIncludesReports(): boolean {
    return !!this.paymentService.getEffectivePlanDetails()?.reportsIncluded;
  }

  private planIncludesLogo(): boolean {
    return !!this.paymentService.getEffectivePlanDetails()?.logoIncluded;
  }

  async ngOnInit() {
    this.spinner.show();
    // Ensure user signal is populated before checking plan entitlements
    await this.userService.getUser();
    const calculationId = localStorage.getItem('calculationId');
    if (calculationId) {
      try {
        const result = await this.api.GetCompanyDetails(calculationId);
        this.paidForReport = this.planIncludesReports() || !!result?.paidForReport;
        this.paidForLogo = this.planIncludesLogo() || !!result?.paidForLogo;
      } catch (e) { }
    }
    this.spinner.hide();
  }

  selectLogo = () => {
    this.logoSelected = true;
  };

  resetLogo = () => {
    this.logoSelected = false;
  };

  async previewReport() {
    localStorage.removeItem('preview_report_sections');

    // Re-check DB for latest paidForReport status
    const calculationId = localStorage.getItem('calculationId');
    if (calculationId) {
      try {
        const result = await this.api.GetCompanyDetails(calculationId);
        this.paidForReport = this.planIncludesReports() || !!result?.paidForReport;
      } catch (e) { }
    }

    if (this.paidForReport) {
      this.close.emit();
      this.preview.emit(true);
    } else {
      await this.redirectToStripe();
    }
  }

  redirectToStripe = async () => {
    this.spinner.show();
    try {
      const [userAttributes, session] = await Promise.all([fetchUserAttributes(), fetchAuthSession()]);
      const token = session.tokens?.idToken?.toString() ?? '';
      const response = await fetch(environment.lambdaUrls.stripe, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          email: userAttributes.email,
          action: 'create_checkout_session',
          mode: 'payment',
          priceId: this.reportPrice,
          successUrl: `${environment.baseUrl}/calculation?paid=${this.reportPrice}`,
          cancelUrl: `${environment.baseUrl}/calculation?paid=nonced`
        })
      });
      const result = await response.json();
      if (!response.ok || !result.url) throw new Error(result.error || 'Failed to create checkout session');
      window.location.href = result.url;
    } catch (error) {
      this.spinner.hide();
      console.error('Error creating checkout session:', error);
    }
  };
}
